import React, { useCallback } from 'react';
import { usePlayerStore } from '../store/playerStore';
import { Button } from './ui/button';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  Trash2,
  Music,
  Video,
  X,
  ListMusic,
  Play,
} from 'lucide-react';

// ─── Single sortable queue item ────────────────────────────────────────────────
function SortableQueueItem({ track, index, onPlay, onRemove, dark }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: track.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  // Theme-specific classes
  const rowBase = dark
    ? 'flex items-center gap-2 px-3 py-2 rounded group cursor-pointer bg-white/5 hover:bg-white/10'
    : 'flex items-center gap-2 px-3 py-2 rounded-lg group cursor-pointer bg-secondary/50 hover:bg-secondary';
  const numCls = dark ? 'text-white/40 text-xs w-4 shrink-0' : 'text-muted-foreground text-xs w-4 shrink-0';
  const gripCls = dark
    ? 'text-white/20 group-hover:text-white/50 cursor-grab active:cursor-grabbing shrink-0'
    : 'text-muted-foreground/30 group-hover:text-muted-foreground cursor-grab active:cursor-grabbing shrink-0';
  const iconBg = dark ? 'w-7 h-7 rounded bg-white/10 flex items-center justify-center shrink-0' : 'w-7 h-7 rounded bg-primary/20 flex items-center justify-center shrink-0';
  const iconCls = dark ? 'w-3.5 h-3.5 text-white/60' : 'w-3.5 h-3.5 text-primary';
  const titleCls = dark ? 'text-white text-sm font-medium truncate leading-tight' : 'text-sm font-medium truncate leading-tight';
  const subtitleCls = dark ? 'text-white/50 text-xs truncate' : 'text-xs text-muted-foreground truncate';
  const removeCls = dark
    ? 'h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100 text-white/40 hover:text-red-400'
    : 'h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive';
  const playCls = dark
    ? 'h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100 text-white/40 hover:text-white'
    : 'h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground';

  return (
    <div ref={setNodeRef} style={style} className={rowBase}>
      {/* Drag handle */}
      <div {...attributes} {...listeners} className={gripCls} onClick={(e) => e.stopPropagation()}>
        <GripVertical className="w-4 h-4" />
      </div>

      {/* Position number */}
      <span className={numCls}>{index + 1}</span>

      {/* Media type icon */}
      <div className={iconBg}>
        {track.media_type === 'video'
          ? <Video className={iconCls} />
          : <Music className={iconCls} />}
      </div>

      {/* Title + creator — clicking plays this item */}
      <div className="flex-1 min-w-0" onClick={() => onPlay(index)}>
        <p className={titleCls}>{track.title}</p>
        <p className={subtitleCls}>{track.creator_name}</p>
      </div>

      {/* Play button (shown on hover) */}
      <Button
        variant="ghost"
        size="icon"
        className={playCls}
        title="Přehrát nyní"
        onClick={(e) => { e.stopPropagation(); onPlay(index); }}
      >
        <Play className="w-3.5 h-3.5" />
      </Button>

      {/* Remove button */}
      <Button
        variant="ghost"
        size="icon"
        className={removeCls}
        title="Odebrat z fronty"
        onClick={(e) => { e.stopPropagation(); onRemove(track.id); }}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

// ─── Main QueuePanel component ─────────────────────────────────────────────────
/**
 * QueuePanel — drag-and-drop queue management panel.
 *
 * Props:
 *   dark     — use dark (video overlay) theme; default false = light (audio bar) theme
 *   onClose  — optional callback for the × close button
 */
export function QueuePanel({ dark = false, onClose }) {
  const currentTrack  = usePlayerStore(s => s.currentTrack);
  const queue         = usePlayerStore(s => s.queue);
  const moveInQueue   = usePlayerStore(s => s.moveInQueue);
  const removeFromQueue = usePlayerStore(s => s.removeFromQueue);
  const clearQueue    = usePlayerStore(s => s.clearQueue);
  const playFromQueue = usePlayerStore(s => s.playFromQueue);

  // dnd-kit sensors — pointer for mouse/touch, keyboard for a11y
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = queue.findIndex(t => t.id === active.id);
    const newIndex = queue.findIndex(t => t.id === over.id);
    if (oldIndex !== -1 && newIndex !== -1) moveInQueue(oldIndex, newIndex);
  }, [queue, moveInQueue]);

  // Theme tokens
  const headerCls = dark
    ? 'flex items-center justify-between px-4 py-3 border-b border-white/10'
    : 'flex items-center justify-between px-3 py-2.5 border-b border-border';
  const titleCls = dark ? 'text-white font-medium text-sm' : 'font-medium text-sm';
  const countCls = dark ? 'text-white/40 text-xs' : 'text-muted-foreground text-xs';
  const clearCls = dark
    ? 'text-white/50 hover:text-white text-xs h-7 px-2'
    : 'text-muted-foreground hover:text-foreground text-xs h-7 px-2';
  const closeCls = dark ? 'h-7 w-7 text-white/50 hover:text-white' : 'h-7 w-7';
  const emptyCls = dark
    ? 'text-white/30 text-sm text-center py-8 px-4'
    : 'text-muted-foreground text-sm text-center py-8 px-4';

  // Current track header strip (shows what's playing now)
  const nowCls = dark
    ? 'px-4 py-2 border-b border-white/10 flex items-center gap-2'
    : 'px-3 py-2 border-b border-border flex items-center gap-2 bg-primary/5';
  const nowDotCls = dark
    ? 'w-1.5 h-1.5 rounded-full bg-primary animate-pulse shrink-0'
    : 'w-1.5 h-1.5 rounded-full bg-primary animate-pulse shrink-0';
  const nowLabelCls = dark ? 'text-white/40 text-xs shrink-0' : 'text-muted-foreground text-xs shrink-0';
  const nowTitleCls = dark ? 'text-white/80 text-xs truncate' : 'text-xs font-medium truncate';

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className={headerCls}>
        <div className="flex items-center gap-2">
          <ListMusic className={dark ? 'w-4 h-4 text-white/60' : 'w-4 h-4 text-muted-foreground'} />
          <span className={titleCls}>Fronta přehrávání</span>
          {queue.length > 0 && (
            <span className={countCls}>({queue.length})</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {queue.length > 0 && (
            <Button variant="ghost" size="sm" className={clearCls} onClick={clearQueue}>
              Vymazat vše
            </Button>
          )}
          {onClose && (
            <Button variant="ghost" size="icon" className={closeCls} onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Now playing strip */}
      {currentTrack && (
        <div className={nowCls}>
          <div className={nowDotCls} />
          <span className={nowLabelCls}>Nyní:</span>
          <span className={nowTitleCls}>{currentTrack.title}</span>
        </div>
      )}

      {/* Queue list */}
      <div className="flex-1 overflow-y-auto overscroll-contain">
        {queue.length === 0 ? (
          <p className={emptyCls}>
            Fronta je prázdná.<br />
            <span className="text-xs opacity-70">Přidejte epizody tlačítkem&nbsp;
              <ListMusic className="inline w-3 h-3" /> na kartě epizody.
            </span>
          </p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={queue.map(t => t.id)} strategy={verticalListSortingStrategy}>
              <div className="p-2 space-y-1">
                {queue.map((track, index) => (
                  <SortableQueueItem
                    key={track.id}
                    track={track}
                    index={index}
                    dark={dark}
                    onPlay={playFromQueue}
                    onRemove={removeFromQueue}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Footer hint */}
      {queue.length > 1 && (
        <div className={dark ? 'px-4 py-2 border-t border-white/10 text-white/25 text-xs text-center' : 'px-3 py-2 border-t border-border text-muted-foreground/50 text-xs text-center'}>
          Přetáhněte položky pro změnu pořadí
        </div>
      )}
    </div>
  );
}
